import { apiClient } from '@reelplicate/api-client';
import { downloadMediaAsset } from '@reelplicate/api-client/media';
import type { ApiErrorDto } from '@reelplicate/api-client/model';
import { toast } from '@reelplicate/ui';
import { isAxiosError } from 'axios';
import { useState } from 'react';

import { getErrorMessage } from '@/lib/errors';

import type { Asset } from './types';

/** Kinds that are one file, saved straight from storage. */
const ONE_FILE = new Set<Asset['kind']>(['reel', 'clip', 'image', 'audio']);

/** Clicks a link that saves rather than opens. */
function save(href: string, name: string) {
  const link = document.createElement('a');
  link.href = href;
  link.download = name;
  link.rel = 'noopener';
  document.body.append(link);
  link.click();
  link.remove();
}

/** The zip's name from its Content-Disposition. */
function zipName(disposition: unknown): string {
  const match = typeof disposition === 'string' && /filename="([^"]+)"/.exec(disposition);
  return match ? match[1]! : 'reelplicate.zip';
}

/** A zip request's error arrives as a Blob: read its JSON message. */
async function zipError(error: unknown): Promise<string> {
  if (isAxiosError(error) && error.response?.data instanceof Blob) {
    try {
      const body = JSON.parse(await error.response.data.text()) as ApiErrorDto;
      const message = Array.isArray(body.message)
        ? body.message.join(', ')
        : body.message;
      if (message) return message;
    } catch {
      // Not JSON: fall through.
    }
  }
  return getErrorMessage(error);
}

/**
 * Downloads library items: one video, image or audio file saves as itself;
 * anything else (several items, a carousel, a caption) as one zip.
 */
export function useDownload(workspaceId: string) {
  const [pending, setPending] = useState(false);

  const download = async (items: Asset[]) => {
    if (items.length === 0 || pending) return;
    setPending(true);
    const [only] = items;
    try {
      if (items.length === 1 && only && ONE_FILE.has(only.kind)) {
        try {
          const { url, name } = await downloadMediaAsset(workspaceId, only.id);
          save(url, name);
        } catch (error) {
          toast.error(getErrorMessage(error));
        }
        return;
      }
      const preparing = toast.loading(
        `Preparing ${items.length} ${items.length === 1 ? 'item' : 'items'}…`,
      );
      try {
        const response = await apiClient.request<Blob>({
          url: `/api/v1/workspaces/${workspaceId}/media/downloads`,
          method: 'POST',
          data: { assetIds: items.map((item) => item.id) },
          responseType: 'blob',
        });
        const href = URL.createObjectURL(response.data);
        save(href, zipName(response.headers['content-disposition']));
        // Let the save start before the blob goes.
        setTimeout(() => URL.revokeObjectURL(href), 60_000);
        const skipped = Number(response.headers['x-skipped'] ?? 0);
        if (skipped > 0) {
          toast.warning(
            `${skipped} ${skipped === 1 ? 'item had' : 'items had'} no file and ${skipped === 1 ? 'was' : 'were'} skipped`,
            { id: preparing },
          );
        } else toast.dismiss(preparing);
      } catch (error) {
        toast.error(await zipError(error), { id: preparing });
      }
    } finally {
      setPending(false);
    }
  };

  return { download, pending };
}
